import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';

import en from '../../i18n/translations/en.json';
import sv from '../../i18n/translations/sv.json';
import da from '../../i18n/translations/da.json';

type TranslationTree = Record<string, string | TranslationTree>;
const mergeWithEnglish = (localeTree: TranslationTree, englishTree: TranslationTree): TranslationTree => {
  const merged: TranslationTree = { ...englishTree };
  Object.keys(localeTree).forEach((key) => {
    const localeValue = localeTree[key];
    const englishValue = englishTree[key];
    if (
      localeValue &&
      typeof localeValue === 'object' &&
      !Array.isArray(localeValue) &&
      englishValue &&
      typeof englishValue === 'object' &&
      !Array.isArray(englishValue)
    ) {
      merged[key] = mergeWithEnglish(localeValue as TranslationTree, englishValue as TranslationTree);
      return;
    }
    merged[key] = localeValue;
  });
  return merged;
};

const translations = {
  en,
  sv: mergeWithEnglish(sv as TranslationTree, en as TranslationTree),
  da: mergeWithEnglish(da as TranslationTree, en as TranslationTree),
};
type LocaleKey = keyof typeof translations;

const COOKIE_NAME = 'locale';

const readLocaleCookie = (): LocaleKey | undefined => {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${COOKIE_NAME}=`));
  if (!match) return undefined;
  const value = match.split('=')[1];
  if ((translations as Record<string, TranslationTree>)[value]) {
    return value as LocaleKey;
  }
  return undefined;
};

const persistLocaleCookie = (locale: LocaleKey) => {
  if (typeof document === 'undefined') return;
  const maxAgeDays = 180;
  document.cookie = `${COOKIE_NAME}=${locale};path=/;max-age=${maxAgeDays * 24 * 60 * 60}`;
};

const resolveLocale = (): LocaleKey => {
  const saved = readLocaleCookie();
  if (saved) return saved;
  const locale = (Intl.DateTimeFormat().resolvedOptions().locale ?? 'en').split('-')[0];
  if ((translations as Record<string, TranslationTree>)[locale]) {
    return locale as LocaleKey;
  }
  return 'en';
};

const getFromTree = (tree: TranslationTree | undefined, path: string): string | undefined => {
  if (!tree) return undefined;
  return path.split('.').reduce<TranslationTree | string | undefined>((acc, key) => {
    if (typeof acc === 'string') return acc;
    return (acc as TranslationTree | undefined)?.[key];
  }, tree) as string | undefined;
};

const formatWithParams = (value: string, params?: Record<string, string | number>): string => {
  if (!params) return value;
  return Object.keys(params).reduce((acc, key) => {
    const pattern = new RegExp(`{${key}}`, 'g');
    return acc.replace(pattern, String(params[key]));
  }, value);
};

const translate = (locale: LocaleKey, key: string, params?: Record<string, string | number>): string => {
  const localeValue = getFromTree(translations[locale], key);
  const fallbackValue = getFromTree(translations.en, key);
  const resolved = localeValue ?? fallbackValue;
  if (!resolved) return key;
  return formatWithParams(resolved, params);
};

/**
 * The same key resolved in every bundled locale, deduped.
 *
 * For matching text that was *generated* under someone else's locale and then stored — an AI
 * player named "Spöket Anders" keeps that name when an English-speaking player opens the round, so
 * matching it against the current locale's prefix alone finds nothing.
 */
export const translateEveryLocale = (key: string, params?: Record<string, string | number>): string[] => {
  const values = (Object.keys(translations) as LocaleKey[]).map((locale) => translate(locale, key, params));
  return [...new Set(values)];
};

let currentLocale: React.MutableRefObject<LocaleKey> = { current: resolveLocale() };

const I18nContext = createContext<{
  locale: LocaleKey;
  setLocale: (locale: LocaleKey) => void;
  t: typeof t;
  availableLocales: LocaleKey[];
}>({
  locale: 'en',
  setLocale: () => undefined,
  t: (key: string, params?: Record<string, string | number>) => translate('en', key, params),
  availableLocales: Object.keys(translations) as LocaleKey[],
});

export const I18nProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [locale, setLocale] = useState<LocaleKey>(resolveLocale());
  const currentLocaleRef = useRef<LocaleKey>(locale);
  const didMountRef = useRef(false);

  // Keep the module-level helpers in sync for callers importing `t` directly.
  currentLocaleRef.current = locale;
  currentLocale = currentLocaleRef;

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      persistLocaleCookie(locale);
      return;
    }
    persistLocaleCookie(locale);
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.reload();
    }
  }, [locale]);

  const translateFn = useCallback(
    (key: string, params?: Record<string, string | number>) => translate(locale, key, params),
    [locale]
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t: translateFn,
      availableLocales: Object.keys(translations) as LocaleKey[],
    }),
    [locale, translateFn]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);

export const t = (key: string, params?: Record<string, string | number>): string => {
  return translate(currentLocale.current, key, params);
};

export const locale = (): LocaleKey => currentLocale.current;

// Expo Router expects a default export; this route file is configuration-only.
export default function I18nRoute() {
  return null;
}
